import React, { useMemo, useState } from "react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  AUDIT_WINDOW,
  CAPTURED_AT,
  MISSING_INTEGRATIONS,
  OPPORTUNITIES,
  PROVIDERS,
  RISKS,
  SUMMARY,
} from "../data";
import { formatMoney } from "../format";

const CONFIDENCE_ORDER = {
  CONFIRMED: 0,
  "CONFIRMED-inactive": 1,
  RECONSTRUCTED: 2,
  ESTIMATED: 3,
  UNKNOWN: 4,
};

const CATEGORIES = ["All", ...new Set(PROVIDERS.map((p) => p.category))];

function ConfidenceBadge({ confidence }) {
  const cls = confidence.startsWith("CONFIRMED")
    ? "confirmed"
    : confidence === "RECONSTRUCTED"
      ? "reconstructed"
      : confidence === "ESTIMATED"
        ? "estimated"
        : "unknown";
  return <span className={`co-badge co-badge--${cls}`}>{confidence}</span>;
}

/**
 * The curated vendor catalog: everything billable that is NOT live-traced AI
 * and NOT GCP infrastructure. Costs here are point-in-time figures from the
 * audit, or Unknown where no billing access exists.
 */
export function VendorBreakdown() {
  const [category, setCategory] = useState("All");
  const [expanded, setExpanded] = useState(null);

  const rows = useMemo(() => {
    const filtered =
      category === "All"
        ? PROVIDERS
        : PROVIDERS.filter((p) => p.category === category);
    return [...filtered].sort((a, b) => {
      const aKnown = a.cost30d !== null;
      const bKnown = b.cost30d !== null;
      if (aKnown && bKnown) return b.cost30d - a.cost30d;
      if (aKnown !== bKnown) return aKnown ? -1 : 1;
      return CONFIDENCE_ORDER[a.confidence] - CONFIDENCE_ORDER[b.confidence];
    });
  }, [category]);

  const knownCount = PROVIDERS.filter((p) => p.cost30d !== null).length;

  return (
    <div>
      <div className="co-disclaimer" style={{ marginBottom: 16 }}>
        <WarningAmberIcon fontSize="small" />
        <span>
          Curated snapshot, not a live feed. Window {AUDIT_WINDOW}, captured{" "}
          {CAPTURED_AT}. Only {SUMMARY.providersWithConfirmedBilling} of{" "}
          {SUMMARY.providersFoundInCode} providers found in code has confirmed
          billing access (OpenAI); {knownCount} have any cost figure at all.
          Everything else is reconstructed, estimated, or unknown.
        </span>
      </div>

      <div className="co-filters" style={{ marginBottom: 12 }}>
        {CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            className={`co-filter-chip ${category === c ? "is-active" : ""}`}
            onClick={() => setCategory(c)}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="ou-table-wrap">
        <table className="ou-table co-provider-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Service</th>
              <th>Category</th>
              <th>Cost (30d)</th>
              <th>Confidence</th>
              <th>Prod?</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const key = `${p.provider}::${p.service}`;
              const isOpen = expanded === key;
              return (
                <React.Fragment key={key}>
                  <tr
                    className="is-clickable"
                    onClick={() => setExpanded(isOpen ? null : key)}
                  >
                    <td>{p.provider}</td>
                    <td>{p.service}</td>
                    <td>{p.category}</td>
                    <td>
                      {p.cost30d === null ? (
                        <span className="co-unknown">Unknown</span>
                      ) : (
                        formatMoney(p.cost30d)
                      )}
                    </td>
                    <td>
                      <ConfidenceBadge confidence={p.confidence} />
                    </td>
                    <td>{p.production ? "Yes" : "No"}</td>
                  </tr>
                  {isOpen ? (
                    <tr className="co-detail-row">
                      <td colSpan={6}>
                        <div className="co-detail">
                          <p>
                            <strong>Primary use:</strong> {p.primaryUse}
                          </p>
                          <p>
                            <strong>Billing method:</strong> {p.billingMethod}
                          </p>
                          {p.activeModels?.length ? (
                            <p>
                              <strong>Active models:</strong>{" "}
                              {p.activeModels.join(", ")}
                            </p>
                          ) : null}
                          <p>
                            <strong>Notes:</strong> {p.notes}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="co-columns" style={{ marginTop: 28 }}>
        <div className="co-section">
          <h2>Top risks</h2>
          <ul className="co-list">
            {RISKS.map((r) => (
              <li key={r.id} className="co-list-item co-list-item--risk">
                <span className="co-list-id">{r.id}</span>
                <div>
                  <p className="co-list-title">{r.title}</p>
                  <p className="co-list-detail">{r.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="co-section">
          <h2>Cost-saving opportunities</h2>
          <ul className="co-list">
            {OPPORTUNITIES.map((o) => (
              <li key={o.id} className="co-list-item co-list-item--opp">
                <span
                  className={`co-priority co-priority--${o.priority.slice(0, 2)}`}
                >
                  {o.priority}
                </span>
                <div>
                  <p className="co-list-title">
                    {o.provider} — {o.savings}
                  </p>
                  <p className="co-list-detail">{o.summary}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="co-section">
        <h2>Missing billing integrations</h2>
        <div className="ou-table-wrap">
          <table className="ou-table">
            <thead>
              <tr>
                <th>Provider / system</th>
                <th>Priority</th>
                <th>Why it matters</th>
              </tr>
            </thead>
            <tbody>
              {MISSING_INTEGRATIONS.map((m) => (
                <tr key={m.provider}>
                  <td>{m.provider}</td>
                  <td>
                    <span
                      className={`co-badge co-badge--${m.priority.toLowerCase()}`}
                    >
                      {m.priority}
                    </span>
                  </td>
                  <td>{m.why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
